import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { UserPlus, Pencil, Ban, CheckCircle2, Trash2 } from "lucide-react";

export default function StaffManagement() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", role: "staff" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm({ name: "", email: "", mobile: "", role: "staff" }); setDialog(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, email: s.email, mobile: s.mobile || "", role: s.role }); setDialog(true); };

  const save = async () => {
    if (!form.name.trim() || (!editing && !form.email.trim())) return toast.error("Name and email are required");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/staff/${editing.user_id}`, { name: form.name.trim(), mobile: form.mobile, role: form.role });
        toast.success("Staff updated");
      } else {
        await api.post("/staff", { ...form, name: form.name.trim(), email: form.email.trim() });
        toast.success("Staff account added — they can now sign in with Google");
      }
      setDialog(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleActive = async (s) => {
    try {
      await api.post(`/staff/${s.user_id}/active`, { active: !s.active });
      toast.success(s.active ? "Account deactivated" : "Account activated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/staff/${s.user_id}`);
      toast.success("Staff account deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="staff-management-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Staff Management</h1>
        <Button onClick={openAdd} data-testid="add-staff-button" className="font-semibold"><UserPlus className="h-4 w-4 mr-1.5" /> Add Staff</Button>
      </div>

      <Card className="mt-4">
        <div className="overflow-x-auto">
          <Table data-testid="staff-table">
            <TableHeader>
              <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Since</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {staff.map((s) => (
                <TableRow key={s.user_id} data-testid={`staff-row-${s.user_id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-sm">{s.email}</TableCell>
                  <TableCell><span className={`status-pill ${s.role === "admin" ? "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-900" : s.role === "operator" ? "bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-950/60 dark:text-sky-300 dark:border-sky-900" : "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"}`}>{s.role}</span></TableCell>
                  <TableCell data-testid={`staff-status-${s.user_id}`}>
                    {s.active
                      ? <span className="status-pill bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-900">Active</span>
                      : <span className="status-pill bg-red-50 text-red-700 border-red-200 dark:bg-red-950/60 dark:text-red-300 dark:border-red-900">Inactive</span>}
                  </TableCell>
                  <TableCell className="text-xs">{fmtDate(s.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.user_id}`}><Pencil className="h-4 w-4" /></Button>
                      {s.user_id !== user?.user_id && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => toggleActive(s)} data-testid={`toggle-staff-${s.user_id}`}>
                            {s.active ? <Ban className="h-4 w-4 text-red-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`delete-staff-${s.user_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {s.name}?</AlertDialogTitle>
                                <AlertDialogDescription>This permanently removes their account and ends all sessions. Their created jobs remain for audit.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid={`delete-staff-cancel-${s.user_id}`}>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => remove(s)} data-testid={`delete-staff-confirm-${s.user_id}`} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Staff" : "Add Staff Member"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Full Name *</Label><Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1" /></div>
            {!editing && <div><Label>Google Email *</Label><Input data-testid="staff-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 mt-1" placeholder="staff@gmail.com" />
              <p className="text-xs text-muted-foreground mt-1">They will sign in with this Google account.</p></div>}
            <div><Label>Mobile</Label><Input data-testid="staff-mobile-input" inputMode="numeric" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 15) })} className="h-11 mt-1" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role-select" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff" data-testid="role-option-staff">Staff</SelectItem>
                  <SelectItem value="operator" data-testid="role-option-operator">Operator</SelectItem>
                  <SelectItem value="admin" data-testid="role-option-admin">Admin</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="save-staff-button" className="w-full sm:w-auto">{saving ? "Saving…" : editing ? "Save Changes" : "Add Staff"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
